import React, { useState, useEffect } from 'react';
import { FiTarget, FiTrendingUp, FiTrendingDown, FiBarChart2, FiDownload } from 'react-icons/fi';
import { formatINR } from '../../utils/currency';

const SalesTargetReports = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');
  const [salesData, setSalesData] = useState({
    monthly: {
      target: 3000000,
      achieved: 2500000,
      percentage: 83.3
    },
    quarterly: {
      target: 9000000,
      achieved: 7500000,
      percentage: 83.3
    },
    yearly: {
      target: 36000000,
      achieved: 30000000,
      percentage: 83.3
    }
  });

  const salesTargets = [
    { product: 'Paracetamol 500mg', target: 50000, achieved: 45000, unit: 'units' },
    { product: 'Amoxicillin 250mg', target: 30000, achieved: 28000, unit: 'units' },
    { product: 'Ibuprofen 400mg', target: 40000, achieved: 35000, unit: 'units' },
    { product: 'Aspirin 100mg', target: 25000, achieved: 22000, unit: 'units' }
  ];

  const distributorPerformance = [
    { name: 'MediDistributors Ltd', target: 500000, achieved: 450000, percentage: 90 },
    { name: 'PharmaSupply Co', target: 400000, achieved: 380000, percentage: 95 },
    { name: 'HealthCare Distributors', target: 300000, achieved: 250000, percentage: 83.3 }
  ];

  const currentData = salesData[selectedPeriod];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <FiTarget className="text-blue-600" />
            Sales Target & Reports
          </h1>
          <p className="text-gray-600 mt-1">Track sales performance against targets</p>
        </div>
        <div className="flex gap-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="yearly">Yearly</option>
          </select>
          <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2">
            <FiDownload /> Export
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-700">Sales Target</h3>
            <FiTarget className="text-blue-600" />
          </div>
          <p className="text-3xl font-bold text-gray-800">{formatINR(currentData.target)}</p>
          <p className="text-sm text-gray-600 mt-2">For {selectedPeriod} period</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-700">Achieved</h3>
            <FiTrendingUp className="text-green-600" />
          </div>
          <p className="text-3xl font-bold text-green-600">{formatINR(currentData.achieved)}</p>
          <p className="text-sm text-gray-600 mt-2">{currentData.percentage}% of target</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-700">Remaining</h3>
            <FiBarChart2 className="text-orange-600" />
          </div>
          <p className="text-3xl font-bold text-orange-600">
            {formatINR(currentData.target - currentData.achieved)}
          </p>
          <p className="text-sm text-gray-600 mt-2">To reach target</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Product-wise Targets</h2>
          <div className="space-y-4">
            {salesTargets.map((item, index) => {
              const percentage = (item.achieved / item.target) * 100;
              return (
                <div key={index}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">{item.product}</span>
                    <span className={`text-sm font-bold ${percentage >= 100 ? 'text-green-600' : 'text-orange-600'}`}>
                      {item.achieved.toLocaleString()} / {item.target.toLocaleString()} {item.unit}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${percentage >= 100 ? 'bg-green-600' : 'bg-orange-600'}`}
                      style={{ width: `${Math.min(percentage, 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{percentage.toFixed(1)}% achieved</p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Distributor Performance</h2>
          <div className="space-y-4">
            {distributorPerformance.map((distributor, index) => {
              const percentage = distributor.percentage;
              return (
                <div key={index}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">{distributor.name}</span>
                    <span className={`text-sm font-bold ${percentage >= 90 ? 'text-green-600' : 'text-orange-600'}`}>
                      {formatINR(distributor.achieved)} / {formatINR(distributor.target)}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${percentage >= 90 ? 'bg-green-600' : 'bg-orange-600'}`}
                      style={{ width: `${Math.min(percentage, 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{percentage.toFixed(1)}% achieved</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Sales Trend</h2>
        <div className="h-64 flex items-center justify-center text-gray-500">
          <div className="text-center">
            <FiBarChart2 className="w-16 h-16 mx-auto mb-2 text-gray-400" />
            <p>Sales trend chart will be displayed here</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesTargetReports;

